import { Amplify } from 'aws-amplify';
import { Hub } from '@aws-amplify/core';
import { Authenticator } from '@aws-amplify/ui-react';
import 'swiper/css';
import * as amplitude from '@amplitude/unified';
import { initAll } from '@amplitude/unified';

import { lazy, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Routes, Route } from 'react-router-dom';
import 'react-lazy-load-image-component/src/effects/blur.css';

import { selectUserEmail, selectUserId } from '../redux/user-data/selectors';
import { getUserData } from '../redux/user-data/operations';
import {
  USER_POOL_ID,
  USER_POOL_CLIENT_ID,
  USER_POOL_DOMAIN,
  USER_POOL_SIGN_IN_URL,
  USER_POOL_SIGN_OUT_URL,
  AMPLITUDE_API_KEY,
} from 'utils/configs';

import PrivateRoute from 'components/PrivateRoute';
import SignInUp from 'pages/auth/SignInUp/SignInUp';
import SharedLayout from 'components/SharedLayout/SharedLayout';
import AuthCallback from 'pages/auth/AuthCallback';
import AuthCallbackEmbedded from 'pages/auth/AuthCallbackEmbedded';

const ProjectListPage = lazy(() => import('pages/ProjectListPage'));

const CreatePage = lazy(() => import('pages/ai-photoshooting/CreatePage'));
const GalleryPage = lazy(() => import('pages/ai-photoshooting/GalleryPage'));
const TaskDetailsPage = lazy(() =>
  import('pages/ai-photoshooting/TaskDetailsPage')
);

const AvatarsCreatePage = lazy(() =>
  import('pages/ai-avatars/AvatarsCreatePage')
);
const AvatarsGalleryPage = lazy(() =>
  import('pages/ai-avatars/AvatarsGalleryPage')
);
const AvatarsTaskDetailsPage = lazy(() =>
  import('pages/ai-avatars/AvatarsTaskDetailsPage')
);

const BgRemoveCreatePage = lazy(() =>
  import('pages/bg-remove/BgRemoveCreatePage')
);
const BgRemoveGalleryPage = lazy(() =>
  import('pages/bg-remove/BgRemoveGalleryPage')
);
const BgRemoveTaskDetailsPage = lazy(() =>
  import('pages/bg-remove/BgRemoveTaskDetailsPage')
);

const ImageUpscalingCreatePage = lazy(() =>
  import('pages/image-upscaling/ImageUpscalingCreatePage')
);
const ImageUpscalingGalleryPage = lazy(() =>
  import('pages/image-upscaling/ImageUpscalingGalleryPage')
);
const ImageUpscalingTaskDetailsPage = lazy(() =>
  import('pages/image-upscaling/ImageUpscalingTaskDetailsPage')
);

const UserCabinetPage = lazy(() => import('pages/UserCabinetPage'));
const PricePage = lazy(() => import('pages/PricePage'));
const Contacts = lazy(() => import('pages/Contacts'));
const TermsPage = lazy(() => import('pages/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('pages/PrivacyPolicyPage'));
const NotFoundPage = lazy(() => import('pages/NotFoundPage'));

const FashionStoreCreatePage = lazy(() =>
  import('pages/fashion-store/FashionStoreCreatePage')
);
const FashionStoreGalleryPage = lazy(() =>
  import('pages/fashion-store/FashionStoreGalleryPage')
);
const FashionStoreOrderDetailsPage = lazy(() =>
  import('pages/fashion-store/FashionStoreOrderDetailsPage')
);

const APP_SIGN_IN_URL = USER_POOL_SIGN_IN_URL || window.location.origin;
const APP_SIGN_OUT_URL = USER_POOL_SIGN_OUT_URL || window.location.origin;

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: USER_POOL_ID,
      userPoolClientId: USER_POOL_CLIENT_ID,
      loginWith: {
        username: true,
        oauth: {
          domain: USER_POOL_DOMAIN,
          scopes: ['email', 'openid', 'profile'],
          redirectSignIn: [
            `${APP_SIGN_IN_URL}/callback`,
            `${APP_SIGN_IN_URL}/auth/callback`,
          ],
          redirectSignOut: [`${APP_SIGN_OUT_URL}/signin`],
          responseType: 'code',
        },
      },
      userAttributes: {
        email: {
          required: true,
        },
      },
    },
  },
});

/**TODO: Remove isTeamsExist */

function App() {
  const userEmail = useSelector(selectUserEmail);
  const userId = useSelector(selectUserId);
  const isTeamsExist = false;
  const dispatch = useDispatch();

  useEffect(() => {
    //** Listening to authorization events from Amplify Hub */
    const hubListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect_failure':
          console.error(
            '❌ Auth Hub Sign in with redirect failed:',
            payload.data
          );
          break;
        case 'tokenRefresh_failure':
          console.error('❌ Auth Hub Token refresh failed');
          break;
        default:
          return;
      }
    });

    return () => hubListener();
  }, []);

  useEffect(() => {
    if (userId) {
      setTimeout(() => {
        dispatch(getUserData());
      }, 2000);
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (window.amplitude?.isInitiated === 'initiated') return;
    if (userId && userId.length > 5 && AMPLITUDE_API_KEY !== '') {
      initAll(AMPLITUDE_API_KEY, {
        logLevel: amplitude.Types.LogLevel.Debug,
        analytics: {
          fetchRemoteConfig: true,
          autocapture: {
            attribution: true,
            fileDownloads: true,
            formInteractions: true,
            pageViews: true,
            sessions: true,
            elementInteractions: true,
            networkTracking: true,
            webVitals: true,
            frustrationInteractions: true,
          },
        },
        sessionReplay: {
          sampleRate: 1,
        },
      });
      window.amplitude = { isInitiated: 'initiated' };

      amplitude.setUserId(userId);
    }
  }, [userId]);

  useEffect(() => {
    var _hsq = (window._hsq = window._hsq || []);

    const today = new Date().toISOString().split('T')[0];
    const lastTrackedDate = localStorage.getItem(
      `lastTrackedDate_${userEmail}`
    );

    _hsq.push([
      'identify',
      {
        email: userEmail,
      },
    ]);

    if (lastTrackedDate !== today && userEmail && userEmail !== '') {
      _hsq.push([
        'trackEvent',
        {
          id: 'page_load',
          value: today,
          email: userEmail,
        },
      ]);

      localStorage.setItem(`lastTrackedDate_${userEmail}`, today);
    }
  }, [userEmail]);

  return (
    <Authenticator.Provider>
      <Routes>
        <Route path="/signin" element={<SignInUp />} />
        <Route path="/callback" element={<AuthCallback />} />
        <Route path="/auth/callback" element={<AuthCallbackEmbedded />} />

        <Route path="/" element={<SharedLayout />}>
          <Route
            index
            element={
              <PrivateRoute>
                <FashionStoreGalleryPage />
              </PrivateRoute>
            }
          />
          <Route path="terms-of-service" element={<TermsPage />} />
          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />

          {isTeamsExist && (
            <Route
              path="project-list"
              element={
                <PrivateRoute>
                  <ProjectListPage />
                </PrivateRoute>
              }
            />
          )}
          <Route
            path="ai-photoshooting"
            element={
              <PrivateRoute>
                <CreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="ai-photoshooting/gallery"
            element={
              <PrivateRoute>
                <GalleryPage />
              </PrivateRoute>
            }
          />
          <Route
            path="ai-photoshooting/details/:taskId"
            element={
              <PrivateRoute>
                <TaskDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="ai-avatars"
            element={
              <PrivateRoute>
                <AvatarsCreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="ai-avatars/gallery"
            element={
              <PrivateRoute>
                <AvatarsGalleryPage />
              </PrivateRoute>
            }
          />
          <Route
            path="ai-avatars/details/:taskId"
            element={
              <PrivateRoute>
                <AvatarsTaskDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="bg-remove"
            element={
              <PrivateRoute>
                <BgRemoveCreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="bg-remove/gallery"
            element={
              <PrivateRoute>
                <BgRemoveGalleryPage />
              </PrivateRoute>
            }
          />
          <Route
            path="bg-remove/details/:taskId"
            element={
              <PrivateRoute>
                <BgRemoveTaskDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="image-upscaling"
            element={
              <PrivateRoute>
                <ImageUpscalingCreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="image-upscaling/gallery"
            element={
              <PrivateRoute>
                <ImageUpscalingGalleryPage />
              </PrivateRoute>
            }
          />
          <Route
            path="image-upscaling/details/:taskId"
            element={
              <PrivateRoute>
                <ImageUpscalingTaskDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="user-cabinet"
            element={
              <PrivateRoute>
                <UserCabinetPage />
              </PrivateRoute>
            }
          />
          <Route
            path="pricing"
            element={
              <PrivateRoute>
                <PricePage />
              </PrivateRoute>
            }
          />
          <Route
            path="help-and-contacts"
            element={
              <PrivateRoute>
                <Contacts />
              </PrivateRoute>
            }
          />
          <Route
            path="ai-fashion-store"
            element={
              <PrivateRoute>
                <FashionStoreCreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="ai-fashion-store/gallery"
            element={
              <PrivateRoute>
                <FashionStoreGalleryPage />
              </PrivateRoute>
            }
          />
          <Route
            path="ai-fashion-store/order/:orderId"
            element={
              <PrivateRoute>
                <FashionStoreOrderDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="*"
            element={
              <PrivateRoute>
                <NotFoundPage />
              </PrivateRoute>
            }
          />
        </Route>
      </Routes>
    </Authenticator.Provider>
  );
}

export default App;
